import { Alert } from '@mui/material';
import { ClientAttributes, PageAttributes } from '@qualifio/sso-proxy-api-client';
import {
  Integration,
  IntegrationCategoryEnum,
  IntegrationStateEnum,
} from '@loyalty/shared-common/integration-api-client';
import { ProgramAttributes } from '@loyalty/shared-common/gamification-api-client';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { UpdateSocialLoginClientBody } from '@loyalty/shared-common/manager-api-client';
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { extractErrorMessage } from '@loyalty/shared-common';
import { Loader } from '../shared/layout/Loader';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';

interface UpdateSocialLoginClientParams {
  body: UpdateSocialLoginClientBody;
  program: ProgramAttributes;
}
export interface LoginPageContextInterface {
  client: UseQueryResult<ClientAttributes | null>;
  setClient: (client: ClientAttributes) => void;
  updateSocialLoginClient: UseMutationResult<
    ProgramAttributes,
    Error,
    UpdateSocialLoginClientParams
  >;
  loginPages: UseQueryResult<PageAttributes[]>;
  integrations: UseQueryResult<Integration[]>;
}

const Context = createContext<LoginPageContextInterface>({} as any);

export function useProgramLoginPage() {
  return useContext(Context);
}

export function ProgramLoginPageProvider({ children }: { children: ReactNode }) {
  const { currentAccount } = useAccounts();
  const { program } = useProgram();
  const { ssoProxyApi, integrationApi, managerApi } = useApi();
  const queryClient = useQueryClient();

  const client = useQuery({
    queryKey: ['client', program.ssoProxyClientId],

    queryFn: () => {
      if (!program.ssoProxyClientId) {
        return null;
      }
      return ssoProxyApi.getClient(program.ssoProxyClientId);
    },

    enabled: false,
  });

  const integrations = useQuery({
    queryKey: ['integrations', program.accountId],

    queryFn: () =>
      integrationApi.getIntegrations(program.accountId, {
        params: {
          category: [IntegrationCategoryEnum.SSO],
          states: [IntegrationStateEnum.Active],
        },
      }),

    enabled: false,
  });

  const loginPages = useQuery({
    queryKey: ['loginPages', currentAccount.id],

    queryFn: () => {
      if (!program.ssoProxyClientId) {
        return [];
      }
      return ssoProxyApi.listPages({ params: { ownerId: `LOY:${currentAccount.id}` } });
    },

    enabled: false,
    placeholderData: [],
  });

  const setClient = useCallback(
    (updatedClient: ClientAttributes) =>
      queryClient.setQueryData(['client', program.ssoProxyClientId], updatedClient),
    [queryClient, program.ssoProxyClientId],
  );

  const updateSocialLoginClient = useMutation({
    mutationFn: async (variables: UpdateSocialLoginClientParams) => {
      const { program, body } = variables;
      if (!program.ssoProxyClientId) {
        throw new Error('Program has no ssoProxyClientId');
      }
      const ssoProxyClient = await ssoProxyApi.getClient(program.ssoProxyClientId);
      await ssoProxyApi.updateClient(program.ssoProxyClientId, {
        version: ssoProxyClient.version,
        pageId: body.pageId,
        integrationId: body.integrationId,
      });
      return program;
    },
  });

  const context = useMemo(
    () => ({ client, loginPages, integrations, setClient, updateSocialLoginClient }),
    [client, loginPages, integrations, setClient, updateSocialLoginClient],
  );

  useEffect(() => {
    if (program.ssoProxyClientId) {
      client.refetch();
    }
  }, [program.ssoProxyClientId]);

  return client.isPending ? (
    <Loader />
  ) : client.error ? (
    <Alert severity="error">{extractErrorMessage(client.error as Error)}</Alert>
  ) : (
    <Context.Provider value={context}>{children}</Context.Provider>
  );
}
